import type { StateCreator } from 'zustand'

import type { CapabilitySlice } from '../capabilitySlice'
import type { ChannelSlice } from '../channelSlice'
import type { DirtySlice } from '../dirtySlice'
import type { EditorSlice } from '../editorSlice'
import type { PostSlice } from '../postSlice/postSlice'
import type { SaveErrorsSlice } from '../saveErrorsSlice'

export type SidePanel =
  | 'aiAssistant'
  | 'notes'
  | 'postPreview'
  | 'hashtagManager'
  | 'templates'
  | 'trendingTopics'

export interface AppState {
  readonly mode: 'create' | 'edit' | 'duplicate'
  // We need to support draftMode to accommodate legacy code that depends on it
  // until we have a better way to determine if we're in draft mode or not
  readonly draftMode: boolean
  readonly previewMode: boolean
  readonly duplicatedFrom?: string
  readonly ideaId?: string
  readonly templateId?: string
  readonly prefillAiPrompt?: string | null
  activeSidePanel: SidePanel | null
  /**
   * Set when the user shows intent to use templates (hover or keyboard focus
   * on the Templates button) or once the panel actually opens, whichever
   * comes first. The templates payload is large, so the panel and its
   * queries stay unmounted until then.
   */
  shouldPrefetchTemplates: boolean
  /**
   * Per-session correlation ID for the Post Groups feature (TS-2026-04).
   * Generated when the store is created. "Save and create another" remounts
   * ComposerProvider via a key change, recreating the store with a fresh ID.
   */
  correlationId: string
  composerTransitionFinished: boolean
}

export interface AppActions {
  setActiveSidePanel: (sidePanel: SidePanel | null) => void
  prefetchTemplates: () => void
  setComposerTransitionFinished: () => void
}

export type AppSlice = AppState & AppActions

export const createAppSlice: StateCreator<
  AppSlice &
    CapabilitySlice &
    ChannelSlice &
    DirtySlice &
    EditorSlice &
    PostSlice &
    SaveErrorsSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  AppSlice
> = (set) => ({
  mode: 'create',
  draftMode: false,
  previewMode: false,
  activeSidePanel: 'postPreview',
  shouldPrefetchTemplates: false,
  correlationId: crypto.randomUUID(),
  composerTransitionFinished: false,

  setActiveSidePanel: (sidePanel): void =>
    set(
      (state) => {
        state.activeSidePanel = sidePanel
        if (sidePanel === 'templates') {
          state.shouldPrefetchTemplates = true
        }
      },
      undefined,
      'appSlice:setActiveSidePanel',
    ),

  prefetchTemplates: (): void =>
    set(
      (state) => {
        state.shouldPrefetchTemplates = true
      },
      undefined,
      'appSlice:prefetchTemplates',
    ),

  setComposerTransitionFinished: (): void =>
    set(
      (state) => {
        state.composerTransitionFinished = true
      },
      undefined,
      'appSlice:setComposerTransitionFinished',
    ),
})
